import styled, { ISpruceThemeProps } from 'styled-components';

export const CalendarWrapper = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
`;

interface ICalendarHeader {
  showExtraMonths?: 'both' | 'next' | 'previous';
}
export const CalendarHeader = styled.div<ICalendarHeader>`
  align-items: center;
  border-bottom: solid 1px ${(props) => props.theme.colors.defaultContentBorder};
  margin-left: ${(props) => props.theme.spacing.spacing5};
  margin-right: ${(props) => props.theme.spacing.spacing5};

  display: grid;
  //setting to roomy so it matches the v2 padding in position: absolute scenarios
  gap: ${(props) => props.theme.spacing.spacing3};
  grid-template-columns: ${(props) =>
    props.showExtraMonths === 'both'
      ? 'min-content 1fr 1fr 1fr min-content'
      : props.showExtraMonths === 'next' || props.showExtraMonths === 'previous'
      ? 'min-content 1fr 1fr min-content'
      : 'min-content 1fr min-content'};
  justify-content: center;
  padding-bottom: ${(props) => props.theme.spacing.spacing5};
  position: relative;
  text-align: left;
`;

export const CalendarContent = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
`;

export enum CalendarDayCondition {
  default = 'default',
  disable = 'disable',
  disabledHighlighted = 'disabled-highlighted',
  selected = 'selected',
  selectedLeft = 'selected-lft',
  selectedRight = 'selected-rt',
  within = 'within',
  highlighted = 'highlighted',
}

interface ICalendarDayProps {
  condition: CalendarDayCondition;
}

const getChildDayColors = (props: ICalendarDayProps & ISpruceThemeProps): string => {
  const styledSelectCss = `
        background-color: ${props.theme.colors.primary};
        color: ${props.theme.colors.white};
        border-radius: 50%;
        &:hover {
                  border-radius: 50%;
                  border-width: 1px;
                  border-style:solid;
                  border-color: ${props.theme.colors.primary}
                  background-color: ${props.theme.colors.white};
                  color: ${props.theme.colors.primary};    
        } `;
  switch (props.condition) {
    case CalendarDayCondition.selectedLeft:
      return styledSelectCss;
    case CalendarDayCondition.selectedRight:
      return styledSelectCss;
  }

  return '';
};

export const CalendarDay = styled.div<ICalendarDayProps>`
  ${(props) => genericDayStyle(props)};
  border-radius: ${(props) => getDayColors(props).borderRadius}
  border: 1px solid transparent;
  text-align: center;
  background-color: ${(props) => getDayColors(props).background};
  color: ${(props) => getDayColors(props).color};
  &:hover {
    border-radius:  ${(props) =>
      props.condition === CalendarDayCondition.within
        ? '0%'
        : props.condition === CalendarDayCondition.selectedLeft
        ? '50% 0% 0% 50%`'
        : props.condition === CalendarDayCondition.selectedRight
        ? '0% 50% 50% 0%'
        : '50%'};
    background-color:  ${(props) => props.theme.colors.primaryOpacity20};
    color:  ${(props) =>
      props.condition === CalendarDayCondition.within
        ? props.theme.colors.white
        : props.theme.colors.defaultContentText};
    cursor: ${(props) =>
      props.condition === CalendarDayCondition.disable || props.condition === CalendarDayCondition.disabledHighlighted
        ? 'not-allowed'
        : 'pointer'};
  }
 & > * {
    ${(props) => getChildDayColors(props)};
    border: 1px solid transparent;
  }
`;

export interface ICalendarDayPlaceHolderProps {
  condition: CalendarDayCondition;
}

export const CalendarDayPlaceHolder = styled.div<ICalendarDayPlaceHolderProps>`
  ${(props) => genericDayStyle(props)};
  background-color: ${(props) =>
    props.condition === CalendarDayCondition.selected
      ? props.theme.colors.primary
      : props.theme.colors.defaultContentBackground};
  color: ${(props) =>
    props.condition === CalendarDayCondition.highlighted || props.condition === CalendarDayCondition.disabledHighlighted
      ? props.theme.colors.primary
      : props.theme.colors.defaultContentBackground};
`;

const genericDayStyle = (props: ICalendarDayProps & ISpruceThemeProps) => {
  const dots: IGetDots = getDots(props);
  return `
  ${props.theme.fonts.mixins.body};
  ${props.condition === CalendarDayCondition.selected ? `border-radius: ${props.theme.spacing.spacing2};` : ''};
  ${
    props.condition === CalendarDayCondition.highlighted || props.condition === CalendarDayCondition.disabledHighlighted
      ? props.theme.fonts.semiBoldWeight
      : ''
  };
  &::after {
    ${dots.content};
    background: ${dots.background};
    width: ${dots.width};
    height: ${dots.height};
    border-radius: ${dots.borderRadius};
    position: ${dots.position};
    left: ${dots.left};
    top: ${dots.top};
    display: ${dots.display};
  }
  `;
};

export const WeekDay = styled.div`
  ${(props) => props.theme.fonts.mixins.body};
  padding: ${(props) => props.theme.spacing.spacing2};
`;

interface IGetDayColors {
  background: string;
  color: string;
  hoverBackground: string;
  hoverColor: string;
  borderRadius: string | null;
}

const getDayColors = (props: ICalendarDayProps & ISpruceThemeProps): IGetDayColors => {
  switch (props.condition) {
    case CalendarDayCondition.selected:
      return {
        background: props.theme.colors.primary,
        color: props.theme.colors.activeControlText,
        hoverBackground: props.theme.colors.activeControlBackground,
        hoverColor: props.theme.colors.activeControlText,
        borderRadius: '50%',
      };
    case CalendarDayCondition.selectedLeft:
      return {
        background: props.theme.colors.primaryOpacity20,
        color: props.theme.colors.activeControlText,
        hoverBackground: props.theme.colors.activeControlBackground,
        hoverColor: props.theme.colors.activeControlText,
        borderRadius: `50% 0% 0% 50%`,
      };
    case CalendarDayCondition.selectedRight:
      return {
        background: props.theme.colors.primaryOpacity20,
        color: props.theme.colors.activeControlText,
        hoverBackground: props.theme.colors.activeControlBackground,
        hoverColor: props.theme.colors.activeControlText,
        borderRadius: `0% 50% 50% 0%`,
      };
    case CalendarDayCondition.within:
      return {
        background: props.theme.colors.primaryOpacity20,
        color: props.theme.colors.defaultContentText,
        hoverBackground: props.theme.colors.activeControlBackground,
        hoverColor: props.theme.colors.activeControlText,
        borderRadius: '0%',
      };
    case CalendarDayCondition.highlighted:
      return {
        background: props.theme.colors.defaultContentBackground,
        color: props.theme.colors.primary,
        hoverBackground: props.theme.colors.activeControlBackground,
        hoverColor: props.theme.colors.defaultContentBackground,
        borderRadius: '50%',
      };
    case CalendarDayCondition.disabledHighlighted:
      return {
        background: props.theme.colors.defaultContentBackground,
        color: props.theme.colors.primary,
        hoverBackground: props.theme.colors.defaultContentBackground,
        hoverColor: props.theme.colors.primary,
        borderRadius: '50%',
      };
    case CalendarDayCondition.disable:
      return {
        background: props.theme.colors.defaultContentBackground,
        color: props.theme.colors.contentPlaceholder,
        hoverBackground: props.theme.colors.defaultContentBackground,
        hoverColor: props.theme.colors.contentPlaceholder,
        borderRadius: '50%',
      };
    case CalendarDayCondition.default:
      return {
        background: props.theme.colors.defaultContentBackground,
        color: props.theme.colors.defaultContentText,
        hoverBackground: props.theme.colors.gray200,
        hoverColor: props.theme.colors.defaultContentBackground,
        borderRadius: '50%',
      };
  }
};

interface IGetDots {
  content?: string;
  background?: string;
  width?: string;
  height?: string;
  borderRadius?: string;
  position?: string;
  left?: string;
  top?: string;
  display?: string;
}

const getDots = (props: ICalendarDayProps & ISpruceThemeProps): IGetDots => {
  switch (props.condition) {
    case CalendarDayCondition.highlighted:
    case CalendarDayCondition.disabledHighlighted:
      return {
        content: "content: '';",
        background: props.theme.colors.primary,
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        position: 'relative',
        left: 'calc(50% - 3px)',
        top: '0px',
        display: 'block',
      };
    default:
      return {};
  }
};
